import React, { useState, useCallback } from 'react'
import { RadioButton, InfiniteList, handleFetchMore } from '@sketch/components'
import { dataIdFromObject } from '@sketch/graphql-cache'
import { FormLabel, RadioLabel } from './WorkspacePanel.styles'
import { ProjectMemberRow } from './WorkspaceMemberRow'
import { AddMemberForm } from 'modules/projects/modals/ProjectSharingModal/ProjectSharingForm'
import {
  WorkspaceMinimalFragment,
  useAddProjectMemberMutation,
  useUpdateProjectPermissionMutation,
  ProjectMembershipFragment,
  WorkspaceMembershipFragment,
} from '@sketch/gql-types'
import { addProjectMembership } from 'modules/projects/operations/utils/projectMemberships.cache'
import { FormValues } from 'modules/projects/modals/AddNewProjectModal/useAutocomplete'
import { ProjectWithShares } from '../types'

interface WorkspacePanelProps {
  project: ProjectWithShares
  workspace: WorkspaceMinimalFragment
  editable?: boolean
  members: ProjectMembershipFragment[]
  workspaceMemberships: WorkspaceMembershipFragment[]
  after?: string | null
  fetchMore: any
}

type WorkspaceAccessLevel = ProjectWithShares['workspaceAccessLevel']

export const WorkspacePanel: React.FC<WorkspacePanelProps> = ({
  project,
  workspace,
  editable = true,
  members,
  after,
  fetchMore,
}) => {
  const [
    workspaceAccessLevel,
    setWorkspaceAccessLevel,
  ] = useState<WorkspaceAccessLevel>(project.workspaceAccessLevel)

  const [updateProjectPermission] = useUpdateProjectPermissionMutation({
    onError: 'show-toast',
    update(_, response) {
      setWorkspaceAccessLevel(
        response.data?.updateProjectPermission.project.workspaceAccessLevel ??
          'DEFAULT'
      )
    },
  })

  const [addMemberToProject] = useAddProjectMemberMutation({
    onError: 'show-toast',
    update(cache, response) {
      if (!response.data?.addProjectMember.projectMembership) {
        return
      }

      addProjectMembership({
        cache,
        projectIdentifier: project.identifier,
        membership: response.data?.addProjectMember.projectMembership,
      })
    },
  })

  const handleChangeWorkspaceAccessLevel = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    await updateProjectPermission({
      variables: {
        projectIdentifier: project.identifier,
        workspaceAccessLevel: e.target.value as WorkspaceAccessLevel,
      },
    })
  }

  const handleAddMember = useCallback(
    async (values: FormValues) => {
      const variables = {
        projectIdentifier: project.identifier,
        email: values.email || undefined,
        workspaceMembershipIdentifier:
          values.workspaceMembershipIdentifier || undefined,
        guestCommentsEnabled: values.guestCommentsEnabled,
        guestInspectEnabled: values.guestInspectEnabled,
      }
      await addMemberToProject({ variables })
    },
    [addMemberToProject, project.identifier]
  )

  return (
    <>
      <RadioLabel>
        Members of <strong>{workspace.name}</strong> workspace:
      </RadioLabel>
      <RadioButton
        name="workspaceAccessLevel"
        label="Can edit or view documents in this project"
        checked={workspaceAccessLevel === 'DEFAULT'}
        value="DEFAULT"
        onChange={handleChangeWorkspaceAccessLevel}
      />
      <RadioButton
        name="workspaceAccessLevel"
        label="Cannot access unless invited to this project"
        checked={workspaceAccessLevel !== 'DEFAULT'}
        value="NONE"
        onChange={handleChangeWorkspaceAccessLevel}
      />

      {workspaceAccessLevel !== 'DEFAULT' && (
        <>
          <FormLabel>
            Invite other <strong>{workspace.name}</strong> members:
          </FormLabel>
          <AddMemberForm
            hideLabel
            workspace={workspace}
            projectIdentifier={project.identifier}
            membersList={members}
            onMemberAdd={handleAddMember}
            filterMembers="Members"
            removeSelf
          />

          <InfiniteList
            canLoadMore={after !== null}
            onLoadMore={handleFetchMore(
              fetchMore,
              ['project', 'memberships', 'entries'],
              {
                dataIdFromObject,
                after,
              }
            )}
          >
            {members.map(member => (
              <ProjectMemberRow
                key={member.identifier}
                member={member}
                project={project}
                editable={editable}
                workspaceIdentifier={workspace.identifier}
              />
            ))}
          </InfiniteList>
        </>
      )}
    </>
  )
}
